import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Fundo vivo do site (tema vermelho): imagem em movimento lento, aurora, grade em perspectiva,
// feixe de luz, brasas e faíscas num canvas, e um facho que segue o mouse.
export default function GlobalBackground() {
  const canvasRef = useRef(null);
  // Dentro do jogo (/game) o fundo animado fica DESLIGADO: o jogo roda num iframe da mesma página e dividia o processador com as brasas e as luzes do site
  // (queda de FPS e travadas enquanto jogava). Fora do jogo ele continua igual.
  const { pathname } = useLocation();
  const inGame = pathname.startsWith("/game");

  useEffect(() => {
    if (inGame) return undefined;
    const cv = canvasRef.current; if (!cv) return undefined;
    const ctx = cv.getContext("2d");
    let W = 0, H = 0, dpr = Math.min(window.devicePixelRatio || 1, 2), raf = 0, alive = true;
    const embers = [], streaks = [];
    const resize = () => { W = window.innerWidth; H = window.innerHeight; cv.width = W * dpr; cv.height = H * dpr; cv.style.width = W + "px"; cv.style.height = H + "px"; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); };
    resize(); window.addEventListener("resize", resize);

    const spawn = (initial) => ({
      x: Math.random() * W, y: initial ? Math.random() * H : H + 20, r: Math.random() * 2.4 + 0.6,
      vy: -(Math.random() * 0.7 + 0.25), sway: Math.random() * 1.5 + 0.4, ph: Math.random() * 6.28, a: Math.random() * 0.6 + 0.35,
      hue: Math.random() < 0.7 ? 350 + Math.random() * 14 : 14 + Math.random() * 18,
    });
    const N = Math.round(Math.min(110, Math.max(50, (W * H) / 20000)));
    for (let i = 0; i < N; i++) embers.push(spawn(true));

    let last = 0, nextStreak = 1800;
    const frame = (t) => {
      if (!alive) return;
      raf = requestAnimationFrame(frame);
      if (document.hidden) return;
      const dt = Math.min(48, t - last || 16); last = t;
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = "lighter";
      for (const e of embers) {
        e.y += e.vy * dt * 0.06 * 16 / 16; e.ph += dt * 0.0016 * e.sway;
        const x = e.x + Math.sin(e.ph) * 26;
        if (e.y < -20) Object.assign(e, spawn(false));
        const fl = 0.65 + 0.35 * Math.sin(t * 0.004 + e.ph * 3);
        const g = ctx.createRadialGradient(x, e.y, 0, x, e.y, e.r * 7);
        g.addColorStop(0, `hsla(${e.hue}, 100%, 62%, ${e.a * fl})`);
        g.addColorStop(0.35, `hsla(${e.hue}, 100%, 50%, ${e.a * fl * 0.35})`);
        g.addColorStop(1, `hsla(${e.hue}, 100%, 45%, 0)`);
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, e.y, e.r * 7, 0, 6.283); ctx.fill();
        ctx.fillStyle = `hsla(${e.hue + 8}, 100%, 82%, ${e.a * fl})`; ctx.beginPath(); ctx.arc(x, e.y, e.r * 0.7, 0, 6.283); ctx.fill();
      }
      nextStreak -= dt;
      if (nextStreak <= 0) { nextStreak = 2200 + Math.random() * 3800; streaks.push({ x: Math.random() * W * 0.9, y: Math.random() * H * 0.45, len: 140 + Math.random() * 160, life: 0 }); }
      for (let i = streaks.length - 1; i >= 0; i--) {
        const s = streaks[i]; s.life += dt / 650; if (s.life >= 1) { streaks.splice(i, 1); continue; }
        const px = s.x + s.life * s.len * 1.6, py = s.y + s.life * s.len * 0.55;
        const g = ctx.createLinearGradient(px - s.len * 0.5, py - s.len * 0.17, px, py);
        g.addColorStop(0, "rgba(255,60,85,0)"); g.addColorStop(1, `rgba(255,190,200,${0.9 * (1 - s.life)})`);
        ctx.strokeStyle = g; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(px - s.len * 0.5, py - s.len * 0.17); ctx.lineTo(px, py); ctx.stroke();
      }
      ctx.globalCompositeOperation = "source-over";
    };
    raf = requestAnimationFrame(frame);

    // facho que segue o mouse + brilho/inclinação dos cartões
    let tilted = null, ticking = false, mx = 0, my = 0, target = null;
    const apply = () => {
      ticking = false;
      const root = document.documentElement.style;
      root.setProperty("--mx", mx + "px"); root.setProperty("--my", my + "px");
      const card = target && target.closest ? target.closest(".pkx-card") : null;
      if (tilted && tilted !== card) { tilted.style.transform = ""; tilted = null; }
      if (card) {
        const r = card.getBoundingClientRect();
        card.style.setProperty("--cx", mx - r.left + "px"); card.style.setProperty("--cy", my - r.top + "px");
        if (card.hasAttribute("data-tilt")) {
          const px = (mx - r.left) / r.width - 0.5, py = (my - r.top) / r.height - 0.5;
          card.style.transform = `perspective(900px) rotateX(${(-py * 7).toFixed(2)}deg) rotateY(${(px * 9).toFixed(2)}deg) translateY(-5px)`; tilted = card;
        }
      }
    };
    const onMove = (e) => { mx = e.clientX; my = e.clientY; target = e.target; if (!ticking) { ticking = true; requestAnimationFrame(apply); } };
    window.addEventListener("mousemove", onMove, { passive: true });

    return () => { alive = false; cancelAnimationFrame(raf); window.removeEventListener("resize", resize); window.removeEventListener("mousemove", onMove); };
  }, [inGame]);

  if (inGame) return <div className="bgx" aria-hidden="true" style={{ background: "#07070b" }} />;
  return (
    <div className="bgx" aria-hidden="true">
      <div className="bgx-img" style={{ backgroundImage: "url('/images/red.png')" }} />
      <div className="bgx-aurora" />
      <div className="bgx-grid" />
      <div className="bgx-beam" />
      <div className="bgx-spot" />
      <canvas id="bgx-canvas" ref={canvasRef} />
      <div className="bgx-noise" />
      <div className="bgx-vignette" />
    </div>
  );
}
